import { Injectable, computed, inject, signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

import { SessionStore } from '../auth/session.store';

/**
 * The languages this portal is built in.
 *
 * Each is named in itself: nobody scanning a menu looks for "Arabic". Adding
 * one here is not enough on its own — `availableLangs` in `app.config.ts` and a
 * file under `assets/i18n` have to agree with it.
 */
export const LANGUAGES: { code: string; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'de', label: 'Deutsch' },
  { code: 'fr', label: 'Français' },
  { code: 'es', label: 'Español' },
  { code: 'hi', label: 'हिन्दी' },
  { code: 'fa', label: 'فارسی' },
  { code: 'ps', label: 'پښتو' },
  { code: 'ar', label: 'العربية' },
];

/**
 * Switching language, in one place.
 *
 * WHY IT IS A SERVICE
 *
 * Two screens offer the choice — the sign-in screen, before there is anybody to
 * remember it for, and the account menu afterwards — and they were about to
 * hold two copies of the list and two copies of the switch. Worse, only one of
 * them was writing the choice down, and nothing was reading it back: choosing
 * Nederlands on the sign-in screen and then signing in returned an English
 * portal, because the active language is a runtime value and the reload after
 * sign-in starts from the default.
 *
 * So: the list lives here, `use()` switches and remembers, and `restore()` runs
 * once before the first paint. `lang` and `dir` on `<html>` follow the active
 * language through their own initializer, so nothing here touches the document.
 */
@Injectable({ providedIn: 'root' })
export class LanguageService {
  private readonly transloco = inject(TranslocoService);
  private readonly session = inject(SessionStore);

  readonly languages = LANGUAGES;

  readonly active = signal(this.transloco.getActiveLang());

  readonly activeLabel = computed(
    () => LANGUAGES.find((one) => one.code === this.active())?.label ?? this.active(),
  );

  /** Switch, and remember for the next visit. */
  use(code: string): void {
    if (!LANGUAGES.some((one) => one.code === code)) return;

    this.transloco.setActiveLang(code);
    this.active.set(code);
    this.session.language = code;
  }

  /**
   * Apply what was remembered, at startup.
   *
   * Silent when there is nothing stored or the stored value is no longer a
   * language this build has — a leftover from a removed locale should fall back
   * to the default rather than leaving the app with no translations at all.
   */
  restore(): void {
    const stored = this.session.language;
    if (!stored || !LANGUAGES.some((one) => one.code === stored)) return;

    this.transloco.setActiveLang(stored);
    this.active.set(stored);
  }
}
